/**
 * Homestead piece catalog: every atomic player-placeable building piece is a
 * committed `buildPiece` procedural-asset doc (kit.ts idiom), compiled
 * client-side by compileAsset and instanced by unique geometry.
 *
 * ⚠ ORDER IS WIRE-BINDING: `Piece.t` (types.ts) indexes PIECE_CATALOG, and
 * placed pieces persist that index. APPEND-ONLY — never reorder or remove
 * entries; retire a piece by keeping its slot (renderers may swap its doc).
 *
 * Snap sockets are TYPED by prefix (edge:/corner:/top:/ground:) — the build-mode
 * snap logic (client) matches ghost sockets to placed-piece sockets by prefix.
 * Palette convention (FIXED, kit.ts): material 0 = body (per-instance tint),
 * 1 = metal trim, 2 = accent, 3 = glass (night-emissive).
 *
 * Pure + pmath-safe: roof pitches are precomputed radian LITERALS (no Math.atan).
 */
import type { AssetModifier, AssetNode, AssetVec3, ProceduralAssetV1 } from "../proceduralAssets.js";
import type { BuildPieceGroup } from "../proceduralAssets.js";
import { pieceCost, type BuildMaterialTier } from "./types.js";
import type { ItemStack } from "../rpg.js";

/** Piece grid unit (matches the settlement module grid — pieces read at world scale). */
export const PIECE_UNIT_M = 3;
/** Storey height (matches LEVEL_H_M). */
export const PIECE_LEVEL_H = 3;

/** atan(0.5) — 1.5 m rise over a 3 m run (shallow roof). Literal: Math.atan is pmath-banned. */
const PITCH_26 = 0.4636476090008061;
/** atan(1) — 45° steep roof. */
const PITCH_45 = 0.7853981633974483;
/** Hypotenuse of the 26° panel: √(3² + 1.5²). */
const SLOPE_LEN_26 = 3.3541019662496847;
/** Hypotenuse of the 45° panel: √(3² + 3²). */
const SLOPE_LEN_45 = 4.242640687119285;

const PALETTE = [
  { color: 0xcfc4ae, roughness: 0.9, metalness: 0.0 },
  { color: 0x7a746a, roughness: 0.6, metalness: 0.4 },
  { color: 0xb35c1e, roughness: 0.8, metalness: 0.1 },
  { color: 0x223038, roughness: 0.2, metalness: 0.0 },
];

/** Furniture palette (settlement FURN_PALETTE lineage — warmer, more varied
 *  than the architectural kit). Slots 0-3 keep the FIXED semantics the bake
 *  relies on: 0 = body (player-tintable), 1 = metal, 2 = accent/fabric,
 *  3 = glass (night-emissive). 4-6 are furniture-only extras. */
const FURN_PALETTE = [
  { color: 0x8a6248, roughness: 0.85, metalness: 0.0 },  // 0 warm wood (tintable body)
  { color: 0x6b6259, roughness: 0.6, metalness: 0.4 },   // 1 metal
  { color: 0xb0562a, roughness: 0.85, metalness: 0.0 },  // 2 fabric / accent
  { color: 0x223038, roughness: 0.2, metalness: 0.0 },   // 3 glass
  { color: 0x54402e, roughness: 0.85, metalness: 0.0 },  // 4 dark wood
  { color: 0xd8cdb4, roughness: 0.9, metalness: 0.0 },   // 5 linen
  { color: 0x5d7d43, roughness: 0.9, metalness: 0.0 },   // 6 foliage / soil growth
];

export interface PieceEntry {
  id: string;
  name: string;
  group: BuildPieceGroup;
  /** Build-material tier — resolves the per-piece cost via pieceCost(). */
  tier: BuildMaterialTier;
  doc: ProceduralAssetV1;
}

let nextId = 0;
const nid = (): string => `n${nextId++}`;
const box = (params: [number, number, number], p: AssetVec3,
  material: number, r: AssetVec3 = [0, 0, 0]): AssetNode =>
  ({ id: nid(), kind: "box", params, p, r, material });
const cyl = (radius: number, height: number, p: AssetVec3, material: number,
  r: AssetVec3 = [0, 0, 0]): AssetNode =>
  ({ id: nid(), kind: "cylinder", params: [radius, radius, height], p, r, material });
const socket = (name: string, p: AssetVec3): AssetNode =>
  ({ id: nid(), name, kind: "socket", p, r: [0, 0, 0] });
const mods = (node: AssetNode, modifiers: AssetModifier[]): AssetNode => ({ ...node, modifiers });

function renumber(nodes: AssetNode[], counter = { i: 0 }): void {
  for (const n of nodes) {
    n.id = `n${counter.i++}`;
    if (n.children) renumber(n.children, counter);
  }
}

function piece(id: string, name: string, group: BuildPieceGroup,
  tier: BuildMaterialTier, nodes: AssetNode[], materials = PALETTE): PieceEntry {
  renumber(nodes);
  return {
    id, name, group, tier,
    doc: {
      version: 1, id, name, kind: "buildPiece", tags: ["homestead"],
      materials, nodes, buildPiece: { group },
    },
  };
}

/** Furniture piece: own warm palette, NO sockets — free-placed (bestSnap has
 *  nothing to match), never a weld-graph node (pieceSocketsWorld is empty);
 *  support is the rest-on-a-floor rule in support.ts. Box nodes collide via
 *  catalogObbs; cylinders are decorative. Local frame: +z = FACING, origin at
 *  the floor (the kit furniture convention). */
function furn(id: string, name: string, tier: BuildMaterialTier, nodes: AssetNode[]): PieceEntry {
  return piece(id, name, "furniture", tier, nodes, FURN_PALETTE);
}

/** Slab snap set (foundation/floor/flat roof), VALHEIM-COMPLETE: corners + edge
 *  midpoints on BOTH faces (top at `y`, bottom at 0 — stack foundations, hang
 *  floors under beams), plus the face centres. Half-extents hw × hd. */
function slabSockets(hw: number, hd: number, y: number): AssetNode[] {
  return [
    // top face
    socket("corner:a", [-hw, y, -hd]), socket("corner:b", [hw, y, -hd]),
    socket("corner:c", [hw, y, hd]), socket("corner:d", [-hw, y, hd]),
    socket("edge:n", [0, y, hd]), socket("edge:s", [0, y, -hd]),
    socket("edge:e", [hw, y, 0]), socket("edge:w", [-hw, y, 0]),
    socket("top:c", [0, y, 0]),
    // bottom face
    socket("corner:ba", [-hw, 0, -hd]), socket("corner:bb", [hw, 0, -hd]),
    socket("corner:bc", [hw, 0, hd]), socket("corner:bd", [-hw, 0, hd]),
    socket("edge:bn", [0, 0, hd]), socket("edge:bs", [0, 0, -hd]),
    socket("edge:be", [hw, 0, 0]), socket("edge:bw", [-hw, 0, 0]),
    socket("ground:c", [0, 0, 0]),
  ];
}

/** Incline snap set (stairs/ramps, width 2·hw, run 2·hd along z, rise `h`):
 *  LOW-end base corners+mid (meets the lower floor edge) and HIGH-end top
 *  corners+mid (meets the upper floor edge) — the Valheim stair contract. */
function inclineSockets(hw: number, hd: number, h: number): AssetNode[] {
  return [
    socket("corner:ll", [-hw, 0, hd]), socket("corner:lr", [hw, 0, hd]),
    socket("edge:low", [0, 0, hd]),
    socket("corner:hl", [-hw, h, -hd]), socket("corner:hr", [hw, h, -hd]),
    socket("edge:high", [0, h, -hd]),
    socket("ground:c", [0, 0, 0]),
  ];
}

/** Wall-face snap set: bottom/top corners + edge midpoints. Wall spans x, rises y. */
function wallSockets(hw: number, h: number): AssetNode[] {
  return [
    socket("corner:bl", [-hw, 0, 0]), socket("corner:br", [hw, 0, 0]),
    socket("corner:tl", [-hw, h, 0]), socket("corner:tr", [hw, h, 0]),
    socket("edge:b", [0, 0, 0]), socket("edge:t", [0, h, 0]),
    socket("edge:l", [-hw, h / 2, 0]), socket("edge:r", [hw, h / 2, 0]),
  ];
}

function make(): PieceEntry[] {
  const U = PIECE_UNIT_M, H = PIECE_LEVEL_H, h2 = U / 2;
  const out: PieceEntry[] = [];
  const add = (e: PieceEntry): void => { out.push(e); };

  // ── foundation (stone) ──  origin = base centre; build surface = top face.
  nextId = 0;
  add(piece("hs-foundation", "Foundation", "foundation", "stone", [
    box([U, 0.5, U], [0, 0.25, 0], 0),
    box([U, 0.1, U], [0, 0.5, 0], 1), // metal deck lip
    ...slabSockets(h2, h2, 0.5),
  ]));
  nextId = 0;
  add(piece("hs-foundation-half", "Half Foundation", "foundation", "stone", [
    box([U / 2, 0.5, U], [0, 0.25, 0], 0),
    box([U / 2, 0.1, U], [0, 0.5, 0], 1),
    ...slabSockets(U / 4, h2, 0.5),
  ]));
  nextId = 0;
  add(piece("hs-post", "Support Post", "foundation", "stone", [
    box([0.3, H, 0.3], [0, H / 2, 0], 0),
    socket("top:c", [0, H, 0]),
    socket("ground:c", [0, 0, 0]),
  ]));

  // ── floor (wood) ──
  nextId = 0;
  add(piece("hs-floor", "Floor", "floor", "wood", [
    box([U, 0.2, U], [0, 0.1, 0], 0),
    ...slabSockets(h2, h2, 0.2),
  ]));
  nextId = 0;
  add(piece("hs-floor-half", "Half Floor", "floor", "wood", [
    box([U / 2, 0.2, U], [0, 0.1, 0], 0),
    ...slabSockets(U / 4, h2, 0.2),
  ]));

  // ── wall (wood) ──  spans x, rises y, thin in z; origin = base centre.
  nextId = 0;
  add(piece("hs-wall", "Wall", "wall", "wood", [
    box([U, H, 0.2], [0, H / 2, 0], 0),
    ...wallSockets(h2, H),
  ]));
  nextId = 0;
  add(piece("hs-wall-half", "Half Wall", "wall", "wood", [
    box([U, H / 2, 0.2], [0, H / 4, 0], 0),
    ...wallSockets(h2, H / 2),
  ]));
  nextId = 0;
  add(piece("hs-wall-window", "Window Wall", "wall", "glass", [
    box([U, H, 0.2], [0, H / 2, 0], 0),
    box([1.8, 1.1, 0.24], [0, 1.85, 0], 3), // glass band
    ...wallSockets(h2, H),
  ]));
  nextId = 0;
  {
    const flank = (U - 1.2) / 2; // panels flanking a 1.2 m × 2.1 m doorway
    add(piece("hs-wall-door", "Doorway Wall", "wall", "wood", [
      box([flank, H, 0.2], [-(1.2 / 2 + flank / 2), H / 2, 0], 0),
      box([flank, H, 0.2], [1.2 / 2 + flank / 2, H / 2, 0], 0),
      box([1.2, H - 2.1, 0.2], [0, 2.1 + (H - 2.1) / 2, 0], 0), // header
      ...wallSockets(h2, H),
    ]));
  }
  nextId = 0;
  add(piece("hs-gate", "Gate Frame", "wall", "metal", [
    box([0.3, H, 0.3], [-h2 + 0.15, H / 2, 0], 1),
    box([0.3, H, 0.3], [h2 - 0.15, H / 2, 0], 1),
    box([U, 0.4, 0.3], [0, H - 0.2, 0], 1), // lintel
    ...wallSockets(h2, H),
  ]));

  // ── roof (wood) ──  pitched panels rotate about x; low edge at z=+hd.
  nextId = 0;
  add(piece("hs-roof-flat", "Flat Roof", "roof", "wood", [
    box([U, 0.2, U], [0, 0.1, 0], 0),
    box([U, 0.08, U], [0, 0.24, 0], 2), // accent cap
    ...slabSockets(h2, h2, 0.2),
  ]));
  nextId = 0;
  add(piece("hs-roof-26", "Pitched Roof 26°", "roof", "wood", [
    // Panel spans the 3 m run rising 1.5 m: rotate a slope-length slab about x.
    box([U, 0.2, SLOPE_LEN_26], [0, 0.75, 0], 0, [PITCH_26, 0, 0]),
    socket("edge:low", [0, 0, h2]),
    socket("edge:high", [0, 1.5, -h2]),
    socket("corner:ll", [-h2, 0, h2]), socket("corner:lr", [h2, 0, h2]),
    socket("corner:hl", [-h2, 1.5, -h2]), socket("corner:hr", [h2, 1.5, -h2]),
    socket("edge:ml", [-h2, 0.75, 0]), socket("edge:mr", [h2, 0.75, 0]),
  ]));
  nextId = 0;
  add(piece("hs-roof-45", "Pitched Roof 45°", "roof", "wood", [
    box([U, 0.2, SLOPE_LEN_45], [0, 1.5, 0], 0, [PITCH_45, 0, 0]),
    socket("edge:low", [0, 0, h2]),
    socket("edge:high", [0, U, -h2]),
    socket("corner:ll", [-h2, 0, h2]), socket("corner:lr", [h2, 0, h2]),
    socket("corner:hl", [-h2, U, -h2]), socket("corner:hr", [h2, U, -h2]),
    socket("edge:ml", [-h2, U / 2, 0]), socket("edge:mr", [h2, U / 2, 0]),
  ]));
  nextId = 0;
  add(piece("hs-roof-cap", "Ridge Cap", "roof", "wood", [
    box([U, 0.25, 0.5], [0, 0.12, 0], 2),
    socket("edge:l", [-h2, 0, 0]), socket("edge:r", [h2, 0, 0]),
    socket("corner:la", [-h2, 0, 0.25]), socket("corner:lb", [-h2, 0, -0.25]),
    socket("corner:ra", [h2, 0, 0.25]), socket("corner:rb", [h2, 0, -0.25]),
    socket("ground:c", [0, 0, 0]),
  ]));

  // ── transit (wood) ──
  nextId = 0;
  add(piece("hs-stair", "Stairs", "transit", "wood", [
    // 4 steps climbing one storey over one unit of run.
    mods(box([1.5, 0.75, 0.75], [0, 0.375, h2 - 0.375], 0),
      [{ type: "array", mode: "linear", count: 4, offset: [0, 0.75, -0.75] }]),
    ...inclineSockets(0.75, h2, H),
  ]));
  nextId = 0;
  add(piece("hs-ramp", "Ramp", "transit", "wood", [
    box([1.5, 0.2, SLOPE_LEN_45], [0, 1.5, 0], 0, [PITCH_45, 0, 0]),
    ...inclineSockets(0.75, h2, U),
  ]));
  nextId = 0;
  add(piece("hs-ladder", "Ladder", "transit", "wood", [
    box([0.1, H, 0.1], [-0.35, H / 2, 0], 1),
    box([0.1, H, 0.1], [0.35, H / 2, 0], 1),
    mods(box([0.8, 0.06, 0.06], [0, 0.3, 0], 1),
      [{ type: "array", mode: "linear", count: 7, offset: [0, 0.4, 0] }]),
    socket("edge:b", [0, 0, 0]),
    socket("edge:t", [0, H, 0]),
    socket("corner:bl", [-0.45, 0, 0]), socket("corner:br", [0.45, 0, 0]),
    socket("corner:tl", [-0.45, H, 0]), socket("corner:tr", [0.45, H, 0]),
  ]));

  // ── structure (wood) ──
  nextId = 0;
  add(piece("hs-pillar", "Pillar", "structure", "wood", [
    box([0.3, H, 0.3], [0, H / 2, 0], 0),
    socket("top:c", [0, H, 0]),
    socket("ground:c", [0, 0, 0]),
  ]));
  nextId = 0;
  add(piece("hs-beam", "Beam", "structure", "wood", [
    box([U, 0.3, 0.3], [0, 0.15, 0], 0),
    socket("edge:l", [-h2, 0.15, 0]),
    socket("edge:r", [h2, 0.15, 0]),
    socket("corner:lt", [-h2, 0.3, 0]), socket("corner:lb", [-h2, 0, 0]),
    socket("corner:rt", [h2, 0.3, 0]), socket("corner:rb", [h2, 0, 0]),
    socket("top:c", [0, 0.3, 0]), socket("ground:c", [0, 0, 0]),
  ]));
  nextId = 0;
  add(piece("hs-railing", "Railing", "structure", "wood", [
    box([U, 0.12, 0.12], [0, 1.0, 0], 0), // top rail
    mods(box([0.1, 1.0, 0.1], [-h2 + 0.1, 0.5, 0], 0),
      [{ type: "array", mode: "linear", count: 5, offset: [(U - 0.2) / 4, 0, 0] }]),
    socket("edge:l", [-h2, 0.5, 0]),
    socket("edge:r", [h2, 0.5, 0]),
    socket("corner:bl", [-h2, 0, 0]), socket("corner:br", [h2, 0, 0]),
    socket("ground:c", [0, 0, 0]),
  ]));

  // ── furniture (2026-08-02, settlement-loop P0 — indices 20+) ──
  // Adapted from the settlement int-* kit (kit.ts FURNITURE) with extents
  // audited for the 3 m homestead module (2.8 m interior clearance): counter
  // narrowed 2.4→2.0, hearth/forge flues shortened below 2.75 m. The bed,
  // hearth, lamp, and station pieces are the P1 dwelling/room-recipe anchors.
  nextId = 0;
  add(furn("hs-bed", "Bed", "wood", [
    box([2.0, 0.35, 1.1], [0, 0.175, 0], 0),               // wood frame
    box([1.9, 0.16, 1.0], [0, 0.43, 0], 5),                // linen mattress
    box([1.1, 0.1, 1.02], [0.35, 0.53, 0], 2),             // fabric blanket (foot +x)
    box([0.55, 0.1, 0.7], [-0.6, 0.56, 0], 5),             // pillow (head −x)
    box([0.08, 0.85, 1.1], [-0.99, 0.425, 0], 0),          // headboard
  ]));
  nextId = 0;
  add(furn("hs-table", "Table", "wood", [
    box([1.6, 0.08, 1.0], [0, 0.86, 0], 0),
    mods(box([0.08, 0.82, 0.08], [0.68, 0.41, 0.38], 4),
      [{ type: "mirror", axis: "x" }, { type: "mirror", axis: "z" }]),
  ]));
  nextId = 0;
  add(furn("hs-stool", "Stool", "wood", [
    box([0.42, 0.07, 0.42], [0, 0.5, 0], 0),
    mods(box([0.06, 0.47, 0.06], [0.15, 0.235, 0.15], 4),
      [{ type: "mirror", axis: "x" }, { type: "mirror", axis: "z" }]),
  ]));
  nextId = 0;
  add(furn("hs-counter", "Counter", "wood", [
    box([2.0, 0.95, 0.66], [0, 0.475, 0], 4),              // dark wood body
    box([2.16, 0.07, 0.82], [0, 1.0, 0], 0),               // warm wood top
    box([2.0, 0.12, 0.05], [0, 0.35, 0.36], 2),            // accent kick rail
  ]));
  nextId = 0;
  add(furn("hs-shelf", "Shelf", "wood", [
    mods(box([0.07, 2.1, 0.45], [0.86, 1.05, 0], 0), [{ type: "mirror", axis: "x" }]),
    box([1.8, 0.06, 0.45], [0, 0.55, 0], 0),
    box([1.8, 0.06, 0.45], [0, 1.15, 0], 0),
    box([1.8, 0.06, 0.45], [0, 1.75, 0], 0),
    box([0.5, 0.3, 0.35], [-0.35, 0.72, 0], 2),            // stacked goods
    box([0.4, 0.25, 0.3], [0.4, 1.3, 0], 5),
  ]));
  nextId = 0;
  add(furn("hs-hearth", "Hearth", "metal", [
    box([1.6, 0.5, 0.85], [0, 0.25, 0], 1),                // stone/metal plinth
    box([1.3, 1.2, 0.65], [0, 1.1, -0.08], 5),             // plastered breast
    box([0.7, 0.55, 0.1], [0, 0.55, 0.3], 2),              // ember glow face
    box([0.8, 1.0, 0.5], [0, 2.2, -0.15], 5),              // flue (top 2.7 < 2.8 clearance)
  ]));
  nextId = 0;
  add(furn("hs-lamp", "Standing Lamp", "metal", [
    cyl(0.16, 0.06, [0, 0.03, 0], 1),
    cyl(0.045, 1.6, [0, 0.83, 0], 1),
    box([0.32, 0.36, 0.32], [0, 1.8, 0], 3),               // glass head (night-emissive)
  ]));
  nextId = 0;
  add(furn("hs-chest", "Chest", "wood", [
    box([0.9, 0.42, 0.52], [0, 0.21, 0], 0),
    box([0.94, 0.12, 0.56], [0, 0.48, 0], 4),              // lid
    box([0.1, 0.2, 0.05], [0, 0.32, 0.28], 1),             // clasp
  ]));
  nextId = 0;
  add(furn("hs-forge", "Forge", "metal", [
    box([1.5, 1.0, 0.9], [0, 0.5, 0], 1),                  // stone/metal block
    box([0.9, 0.35, 0.6], [0, 1.1, -0.05], 2),             // ember bed (accent glow)
    box([1.1, 1.0, 0.7], [0, 1.6, -0.1], 4),               // chimney hood
    box([0.8, 0.6, 0.5], [0, 2.4, -0.1], 4),               // flue (top 2.7)
  ]));
  nextId = 0;
  add(furn("hs-anvil", "Anvil", "metal", [
    box([0.5, 0.45, 0.5], [0, 0.225, 0], 0),               // stump
    box([0.85, 0.28, 0.3], [0, 0.6, 0], 1),                // anvil body
    box([0.3, 0.14, 0.22], [0.45, 0.62, 0], 1),            // horn block
  ]));
  nextId = 0;
  add(furn("hs-workbench", "Workbench", "wood", [
    box([1.8, 0.09, 0.75], [0, 0.85, 0], 0),               // top
    mods(box([0.09, 0.81, 0.09], [0.78, 0.4, 0.28], 4),
      [{ type: "mirror", axis: "x" }, { type: "mirror", axis: "z" }]),
    box([0.4, 0.18, 0.3], [-0.5, 0.98, 0.05], 1),          // vise/tool block
    box([0.55, 0.1, 0.35], [0.45, 0.95, -0.08], 2),        // parts tray
  ]));
  nextId = 0;
  add(furn("hs-stove", "Stove", "metal", [
    box([0.9, 0.85, 0.6], [0, 0.425, 0], 1),
    box([0.5, 0.4, 0.06], [0, 0.42, 0.31], 2),             // fire door
    cyl(0.09, 1.4, [0.25, 1.55, -0.15], 1),                // flue pipe (top 2.25)
  ]));
  nextId = 0;
  add(furn("hs-stall", "Market Stall", "wood", [
    box([2.0, 0.9, 0.7], [0, 0.45, 0], 4),                 // stall body
    box([2.15, 0.07, 0.85], [0, 0.95, 0], 0),              // top board
    mods(box([0.09, 1.9, 0.09], [0.95, 0.95, 0.3], 0), [{ type: "mirror", axis: "x" }]),
    box([2.2, 0.09, 0.9], [0, 1.95, 0.12], 2, [0.18, 0, 0]), // awning board
    box([0.5, 0.25, 0.4], [-0.4, 1.1, 0], 5),              // goods
    box([0.4, 0.2, 0.35], [0.45, 1.08, 0.05], 6),
  ]));
  nextId = 0;
  add(furn("hs-farm-plot", "Farm Plot", "wood", [
    // Raised soil bed — the outdoor farmer station (needs NO roof).
    box([2.4, 0.25, 0.12], [0, 0.125, 1.14], 0),           // rim n
    box([2.4, 0.25, 0.12], [0, 0.125, -1.14], 0),          // rim s
    box([0.12, 0.25, 2.16], [1.14, 0.125, 0], 0),          // rim e
    box([0.12, 0.25, 2.16], [-1.14, 0.125, 0], 0),         // rim w
    box([2.16, 0.2, 2.16], [0, 0.1, 0], 4),                // soil (dark)
    mods(box([0.14, 0.22, 1.9], [-0.7, 0.3, 0], 6),        // sprout rows
      [{ type: "array", mode: "linear", count: 3, offset: [0.7, 0, 0] }]),
  ]));
  nextId = 0;
  add(furn("hs-mining-rig", "Mining Rig", "metal", [
    box([1.8, 0.3, 1.3], [0, 0.15, 0], 1),
    mods(box([0.12, 1.7, 0.12], [0.72, 1.0, 0.48], 1),
      [{ type: "mirror", axis: "x" }, { type: "mirror", axis: "z" }]),
    box([1.5, 0.14, 0.14], [0, 1.75, 0.48], 1),
    box([0.38, 0.7, 0.5], [0, 0.55, -0.2], 2),
    cyl(0.12, 1.2, [0, 1.15, -0.15], 1, [1.5707963267948966, 0, 0]),
  ]));
  nextId = 0;
  add(furn("hs-salvage-rig", "Salvage Rig", "metal", [
    box([2.0, 0.12, 1.0], [0, 0.85, 0], 1),
    mods(box([0.1, 0.82, 0.1], [0.82, 0.41, 0.36], 1),
      [{ type: "mirror", axis: "x" }, { type: "mirror", axis: "z" }]),
    box([0.65, 0.35, 0.55], [-0.45, 1.08, 0], 4),
    box([0.5, 0.22, 0.4], [0.5, 1.02, 0], 2),
  ]));
  nextId = 0;
  add(furn("hs-harvester", "Resource Harvester", "metal", [
    cyl(0.65, 0.25, [0, 0.125, 0], 1),
    cyl(0.18, 1.6, [0, 0.95, 0], 1),
    box([1.4, 0.12, 0.18], [0, 1.55, 0], 2),
    box([0.42, 0.5, 0.32], [0, 0.55, 0.28], 3),
  ]));
  nextId = 0;
  add(furn("hs-admin-desk", "Admin Desk", "wood", [
    box([1.8, 0.1, 0.75], [0, 0.82, 0], 0),
    mods(box([0.09, 0.78, 0.09], [0.75, 0.39, 0.27], 4),
      [{ type: "mirror", axis: "x" }, { type: "mirror", axis: "z" }]),
    box([0.7, 0.48, 0.08], [0, 1.13, -0.08], 3),
    box([0.5, 0.06, 0.3], [0, 0.94, 0.12], 2),
  ]));

  return out;
}

/** ⚠ Index order is wire-binding (`Piece.t`). Append-only. */
export const PIECE_CATALOG: readonly PieceEntry[] = make();

export const PIECES_BY_ID: ReadonlyMap<string, PieceEntry> =
  new Map(PIECE_CATALOG.map((e) => [e.id, e]));

/** Materials one placement of catalog index `t` costs (fresh copies), or null
 *  for an out-of-range index (the server rejects the placement). */
export function pieceCatalogCost(t: number): ItemStack[] | null {
  const e = PIECE_CATALOG[t];
  return e ? pieceCost(e.tier) : null;
}
